import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { colors, fonts, spacing, borderRadius, shadows, tableStyles } from '../constants';
import { useSnackbar } from '../components/common/Snackbar';
import { adminService } from '../services/adminService';

const Roles = () => {
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const stats = await adminService.getStats();
      const roleSummary = stats?.users?.byRole || {};

      const roleEntries = await Promise.all(
        Object.entries(roleSummary).map(async ([roleKey, totalUsers]) => {
          try {
            const [activeResponse, inactiveResponse] = await Promise.all([
              adminService.getUsers({ role: roleKey, isActive: true, page: 1, limit: 1 }),
              adminService.getUsers({ role: roleKey, isActive: false, page: 1, limit: 1 }),
            ]);

            const activeCount = activeResponse?.pagination?.total || 0;
            const inactiveCount = inactiveResponse?.pagination?.total || 0;

            return {
              id: roleKey,
              roleName: roleKey.charAt(0).toUpperCase() + roleKey.slice(1),
              totalUsers,
              activeUsers: activeCount,
              inactiveUsers: inactiveCount,
            };
          } catch (roleError) {
            // console.error(`Failed to fetch counts for role ${roleKey}:`, roleError);
            return {
              id: roleKey,
              roleName: roleKey.charAt(0).toUpperCase() + roleKey.slice(1),
              totalUsers,
              activeUsers: 0,
              inactiveUsers: 0,
            };
          }
        })
      );

      setRoles(roleEntries);
    } catch (error) {
      // console.error('Failed to fetch roles:', error);
      const message = error.response?.data?.message || 'Failed to load roles';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Header cell style with no hover effects
  const headerCellStyle = {
    ...tableStyles.header,
    color: colors.textWhite,
    fontWeight: fonts.weightSemiBold,
    borderBottom: 'none !important',
    borderTop: 'none !important',
    borderLeft: 'none !important',
    borderRight: 'none !important',
    '&:hover': {
      borderBottom: 'none !important',
      borderTop: 'none !important',
      borderLeft: 'none !important',
      borderRight: 'none !important',
      backgroundColor: 'transparent !important',
      outline: 'none',
    },
    '&::before': {
      display: 'none',
    },
    '&::after': {
      display: 'none',
    },
  };

  const getStatusColor = (status) => {
    return status === 'Active' ? colors.success : colors.textSecondary;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, p: { xs: spacing.sm, sm: spacing.md } }}>
      <Box sx={{ width: '100%', maxWidth: 'none' }}>
        <Typography variant="body1" sx={{ 
          color: colors.textSecondary, 
          fontSize: { xs: fonts.fontSizeSm, sm: fonts.fontSizeMd }, 
          mb: spacing.md, 
          mt: 0 
        }}>
          Manage user roles and access levels.
        </Typography>

        <Paper sx={{ borderRadius: borderRadius.md, boxShadow: shadows.md, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: spacing.sm, sm: spacing.md },
              py: spacing.sm,
              background: colors.primaryDark,
              color: colors.textWhite,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: `${borderRadius.md} ${borderRadius.md} 0 0`,
              zIndex: 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: colors.primaryTint,
                filter: 'blur(40px)',
                opacity: 0.9,
                zIndex: 0,
              },
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: fonts.weightSemiBold,
                fontSize: { xs: fonts.fontSizeXs, sm: fonts.fontSizeSm },
                letterSpacing: '0.02em',
                position: 'relative',
                zIndex: 1,
              }}
            >
              Roles {roles.length > 0 && `(${roles.length})`}
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto', borderTop: `2px solid ${colors.borderDefault}` }}>
            <Table sx={{ minWidth: { xs: 600, sm: '100%' } }}>
              <TableHead>
                <TableRow 
                  sx={{ 
                    backgroundColor: colors.primaryDark,
                    '&:hover': { backgroundColor: colors.primaryDark },
                    '& .MuiTableCell-root': {
                      borderBottom: 'none !important',
                      borderTop: 'none !important',
                      borderLeft: 'none !important',
                      borderRight: 'none !important',
                      backgroundColor: colors.primaryDark,
                      '&:hover': { borderBottom: 'none !important', borderTop: 'none !important', borderLeft: 'none !important', borderRight: 'none !important' },
                    },
                  }}
                >
                  <TableCell sx={headerCellStyle}>Role Name</TableCell>
                  <TableCell sx={headerCellStyle}>Total Users</TableCell>
                  <TableCell sx={headerCellStyle}>Active Users</TableCell>
                  <TableCell sx={headerCellStyle}>Inactive Users</TableCell>
                  <TableCell sx={headerCellStyle}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ ...tableStyles.cell, textAlign: 'center', py: spacing.xl }}>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        No role data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((role) => {
                    const statusLabel = role.activeUsers > 0 ? 'Active' : 'Inactive';
                    return (
                      <TableRow 
                        key={role.id} 
                        hover 
                        sx={{ 
                          backgroundColor: colors.backgroundWhite,
                          '&:hover': { 
                            backgroundColor: colors.primaryTint,
                            borderLeft: `3px solid ${colors.primaryDark}`,
                          },
                          '&:nth-of-type(even)': {
                            backgroundColor: colors.backgroundLight,
                          },
                        }}
                      >
                        <TableCell sx={{ ...tableStyles.cell, fontWeight: fonts.weightMedium, color: '#1b2538' }}>
                          {role.roleName}
                        </TableCell>
                        <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>{role.totalUsers}</TableCell>
                        <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>{role.activeUsers}</TableCell>
                        <TableCell sx={{ ...tableStyles.cell, color: '#1b2538' }}>{role.inactiveUsers}</TableCell>
                        <TableCell sx={{ ...tableStyles.cell }}>
                          <Chip
                            label={statusLabel}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(statusLabel) + '20',
                              color: getStatusColor(statusLabel),
                              fontWeight: fonts.weightSemiBold,
                              fontSize: fonts.fontSizeXs,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={roles.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: `1px solid ${colors.borderDefault}`,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: fonts.fontSizeXs,
                color: colors.textSecondary,
              },
              '& .MuiIconButton-root': {
                color: colors.textSecondary,
                '&:hover': {
                  backgroundColor: colors.backgroundSoft,
                },
              },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default Roles;

